import { useEffect, useRef } from 'react';
import { BroadcastChannel } from 'broadcast-channel';

import type { AlfaAuthConfig } from 'src/components/AuthNew/utils/socialNetworkUtils';
import { ALFA_ID_CHANNEL } from './constants';
import { createAlfaAuthUrl } from '../utils/createAlfaAuthUrl';

type AlfaAuthFailPayload = {
  error: string;
  state: string;
};

type AlfaAuthSuccessPayload = {
  state?: string;
  code: string;
  redirect_url: string;
};

export type AlfaAuthPayload = AlfaAuthFailPayload | AlfaAuthSuccessPayload;

export type AlfaAuthResult =
  | {
      success: true;
      payload: AlfaAuthSuccessPayload;
    }
  | {
      success: false;
      payload: AlfaAuthFailPayload;
    };

type AlfaAuthOnSuccess = (result: AlfaAuthResult) => void;
type AlfaAuthOnError = () => void;

/**
 * @desc процесс авторизации следующий:
 * 1. в новом окне открывается ссылка для авторизации на стороне Alfa
 * 2. создается Broadcast канал для обмена сообщениями между окном инициатором авторизации
 * и окном, где происходит запрос кода Alfa
 * 3. после подтверждения доступа, Alfa перенаправляет на redirect_url из ссылки и добавляет code или ошибку в параметры
 * 4. на странице шаблона (app/views/social_login/alfabank.haml) скрипт считывает code или ошибку и отправляет их по Broadcast каналу
 * 5. хук передает полученные данные вызываемому методу
 * */
export const useAlfaAuth = () => {
  const authCodeRef = useRef<AlfaAuthResult | undefined>();
  const authChannel = useRef<BroadcastChannel>();

  const resetAuthCodeRef = (): void => {
    authCodeRef.current = undefined;
  };

  const openAuthPopup = function (url: string): Window | null {
    return window.open(url, '_blank', 'popup');
  };

  const createCloseAuthPopupListener = function (onError: AlfaAuthOnError) {
    return function (instance: Window | null): void {
      if (!instance) {
        return;
      }
      const intID = setInterval(() => {
        if (instance?.closed) {
          clearInterval(intID);
          if (!authCodeRef.current || !authCodeRef.current?.success) {
            closeAuthChannel();
            onError();
          }
        }
      }, 500);
    };
  };

  const createAuthChannelResultHandler = (
    onSuccess: AlfaAuthOnSuccess,
    onError: AlfaAuthOnError,
  ) =>
    function (result: AlfaAuthResult) {
      if (!result?.success) {
        // eslint-disable-next-line no-console
        console.error('Ошибка авторизации: ', result);
        onError();
        return;
      }
      authCodeRef.current = result;
      onSuccess(result);
      closeAuthChannel();
    };
  /**
   * Открыть канал для общения между окном инициатором авторизации
   * и окном в котором происходит получение кода от AlfaBank
   * */
  const openAuthChannel = (handler) => {
    const channel = new BroadcastChannel(ALFA_ID_CHANNEL);
    authChannel.current = channel;
    channel.addEventListener('message', handler);
  };

  const closeAuthChannel = () => {
    authChannel.current?.close();
  };

  const handleAlfaLogin = (
    config: AlfaAuthConfig,
    onSuccess: AlfaAuthOnSuccess,
    onError: AlfaAuthOnError,
  ): void => {
    try {
      closeAuthChannel();
      resetAuthCodeRef();
      const authURL = createAlfaAuthUrl(
        config,
        String(Math.floor(Math.random() * 1000000000)),
      );
      const popup = openAuthPopup(authURL);

      const listenCloseAuthPopup = createCloseAuthPopupListener(onError);
      listenCloseAuthPopup(popup);
      openAuthChannel(createAuthChannelResultHandler(onSuccess, onError));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  /** закрытие канала обмена сообщениями */
  useEffect(() => () => closeAuthChannel(), []);

  return {
    handleAlfaLogin,
  };
};
